/*
 * CHANGEHANDLER.JS - Task Claim Management Handlers for Budget Preview
 * 
 * SUMMARY:
 * Implemented decimal.js library to eliminate floating-point precision errors
 * in all financial calculations. This ensures exact precision for percentages,
 * amounts, and all mathematical operations involving currency values.
 * 
  * MAJOR FUNCTIONS:
 * - updatePercentageChange() - Handles percentage input changes with amount recalculation
 * - updateClaimAmountChange() - Handles amount input changes with percentage recalculation
 * - updateRemainingClaimAmountChange() - Handles remaining claim checkbox changes
 *
 *  KEY FEATURES:
 * - Amount field is always the source of truth (primary input field)
 * - Percentage is calculated from amount for display purposes
 * - Bidirectional calculation between percentage and amount fields
 * - Validation for maximum allowed values and percentage limits
 * - No auto-correction - preserves user's exact input
 * - Consistent totalBefore calculation across all handlers
 * - Precision handling with proper rounding
 * 
 * 
 * USE CASES:
 * - Invoice generation and progress claims
 * - Budget preview and calculation
 * - Task-level claim management
 * - Percentage and amount synchronization
 * - Data integrity preservation
 * - Financial calculations requiring exact precision
 */

import { round } from "lodash";
import { parseNumericValue } from "../../../../../../util/common";
import { numberFormat } from "../../../../../../util/currency-formatter";
import { toDecimal, roundValue, Decimal } from "../../../../../../util/decimal-utils";
import { progressClaim } from "./calculateTotal";

/**
 * Handles percentage input changes and recalculates corresponding claim amount.
 * Always sets amount as the source of truth since amount field is the primary input.
 * Uses Decimal.js for exact precision calculations.
 * 
 * @param {Object} params - Function parameters
 * @param {Event} params.event - Input change event with target.value
 * @param {string|number} params.taskId - Unique identifier of the task
 * @param {Array} params.selectedTask - Array of task objects to update
 * @returns {Object} Updated selectedTask array and task index, or undefined if task not found
 * 
 * @description
 * When user edits the claim percentage field:
 * 1. Validates the input value (0-100% remaining)
 * 2. Calculates corresponding amount from percentage using Decimal.js
 * 3. Sets amount as source of truth (amount field is primary)
 * 4. Updates task selection state based on input
 * 
 * @example
 * const result = updatePercentageChange({
 *   event: { target: { value: '25' } },
 *   taskId: 'task-123',
 *   selectedTask: taskArray
 * });
 * // Returns: { selectedTask: updatedArray, index: 0 }
 */
export const updatePercentageChange = ({ event, taskId, selectedTask, isSectionClaim = false }) => {
    let value = parseFloat(event.target.value);
    // Support section-level claims - look up by stageId when isSectionClaim is true
    const index = isSectionClaim
        ? selectedTask.findIndex((t) => t.stageId === taskId || t.id === taskId)
        : selectedTask.findIndex((t) => t.id === taskId);
    if (index === -1) return { selectedTask, index };

    if (isNaN(value)) value = 0;
    if (value > 100 - selectedTask[index].previousPercentageComplete || value < 0) return;

    // Use the same totalBefore calculation as the render component for consistency
    // For SECTION type, totalBefore is already set from aggregated child data
    const taskTotal = selectedTask[index].type === 'SECTION'
        ? (parseFloat(selectedTask[index].totalBefore) || parseFloat(selectedTask[index].sub_total) || 0)
        : (selectedTask[index].totalBefore ||
            (parseFloat(selectedTask[index].allocatedMaterialCost) || 0) +
            (parseFloat(selectedTask[index].allocatedLaborCost) || 0) +
            (parseFloat(selectedTask[index].allocatedSubContractorCost) || 0));

    // Convert to Decimal for exact precision
    const taskTotalDecimal = toDecimal(taskTotal);
    const valueDecimal = toDecimal(value);
    const previousPercentageDecimal = toDecimal(selectedTask[index].previousPercentageComplete || 0);

    // Calculate amount from percentage (amount is always source of truth)
    const exactAmount = taskTotalDecimal.mul(valueDecimal).div(100);
    const finalAmount = roundValue(exactAmount);

    // Update task with calculated values
    selectedTask[index].remainingPercentage = value; // Store percentage for display
    selectedTask[index].remainingAmount = finalAmount; // Amount is source of truth
    value ? (selectedTask[index].isSelected = true) : (selectedTask[index].isSelected = false);

    // Calculate percentage complete using Decimal
    const percentageComplete = previousPercentageDecimal.plus(valueDecimal);
    selectedTask[index].percentageComplete = roundValue(percentageComplete);

    return { selectedTask, index };
};

/**
 * Handles claim amount input changes and recalculates corresponding percentage.
 * Amount is always the source of truth since it's the primary input field.
 * Uses Decimal.js for exact precision calculations.
 * No auto-correction - preserves user's exact input.
 * 
 * @param {Object} params - Function parameters
 * @param {Event} params.event - Input change event with target.value
 * @param {string|number} params.taskId - Unique identifier of the task
 * @param {Array} params.selectedTask - Array of task objects to update
 * @returns {Object} Updated selectedTask array and task index, or undefined if task not found
 * 
 * @description
 * When user edits the claim amount field:
 * 1. Validates the input value against maximum remaining amount
 * 2. Preserves exact user input in remainingAmount (amount is source of truth)
 * 3. Calculates corresponding percentage from amount using Decimal.js
 * 4. No auto-correction - user sees exactly what they typed
 * 5. Updates task selection state based on input
 * 
 * @example
 * const result = updateClaimAmountChange({
 *   event: { target: { value: '5000' } },
 *   taskId: 'task-123',
 *   selectedTask: taskArray
 * });
 * // Returns: { selectedTask: updatedArray, index: 0 }
 */
/**
 * Handles remaining claim amount input changes.
 * Calculates percentage from amount using Decimal.js for exact precision.
 * Amount is always the source of truth.
 * No validation or auto-correction - preserves user's exact input.
 * 
 * @param {Object} params - Function parameters
 * @param {Event} params.event - Input change event with target.value
 * @param {string|number} params.taskId - Unique identifier of the task
 * @param {Array} params.selectedTask - Array of task objects to update
 * @returns {Object} Updated selectedTask array and task index, or undefined if task not found
 * 
 * @description
 * When user enters a claim amount:
 * 1. Amount field is the source of truth (no validation against max remaining)
 * 2. Percentage is calculated from amount using Decimal.js for precision
 * 3. User's exact input is preserved (no auto-correction or validation)
 * 4. percentageComplete is updated to previous + new percentage
 * 
 * @example
 * const result = updateClaimAmountChange({
 *   event: { target: { value: '9814.00' } },
 *   taskId: 'task-123',
 *   selectedTask: taskArray
 * });
 * // Returns: { selectedTask: updatedArray, index: 0 }
 */
export const updateClaimAmountChange = ({ event, taskId, selectedTask, progressClaimId, progressClaims, isSectionClaim = false }) => {
    // Support section-level claims - look up by stageId when isSectionClaim is true
    const index = isSectionClaim
        ? selectedTask.findIndex((t) => t.stageId === taskId || t.id === taskId)
        : selectedTask.findIndex((t) => t.id === taskId);
    if (index === -1) return { selectedTask, index };

    const inputValue = parseFloat(event.target.value);
    const totalClaimed = progressClaim({ task: selectedTask[index], progressClaims, progressClaimId });

    // Use the same totalBefore calculation as the render component for consistency
    // For SECTION type, totalBefore is already set from aggregated child data
    const taskTotal = selectedTask[index].type === 'SECTION'
        ? (parseFloat(selectedTask[index].totalBefore) || parseFloat(selectedTask[index].sub_total) || 0)
        : (selectedTask[index].totalBefore ||
            (parseFloat(selectedTask[index].allocatedMaterialCost) || 0) +
            (parseFloat(selectedTask[index].allocatedLaborCost) || 0) +
            (parseFloat(selectedTask[index].allocatedSubContractorCost) || 0));

    if (!taskTotal) return; // nothing to compare with

    // Convert to Decimal for exact precision
    const taskTotalDecimal = toDecimal(taskTotal);
    const totalClaimedDecimal = toDecimal(totalClaimed);
    const inputValueDecimal = toDecimal(inputValue);
    const previousPercentageDecimal = toDecimal(selectedTask[index].previousPercentageComplete || 0);

    const remainingAmount = taskTotalDecimal.minus(totalClaimedDecimal);
    const remainingPercentage = remainingAmount.div(taskTotalDecimal).mul(100);

    // Calculate percentage with precise math using Decimal
    const exactPercentage = inputValueDecimal.mul(100).div(taskTotalDecimal);

    // Calculate max remaining using Decimal (for reference only - no validation)
    const maxRemaining = taskTotalDecimal.mul(
        new Decimal(100).minus(previousPercentageDecimal)
    ).div(100);

    inputValue ? (selectedTask[index].isSelected = true) : (selectedTask[index].isSelected = false);

    // Amount is always the source of truth
    selectedTask[index].remainingAmount = inputValue; // Preserve exact user input
    selectedTask[index].remainingClaimAmount = inputValue;
    selectedTask[index].remainingPercentage = exactPercentage.toNumber();

    // Use previousPercentageComplete (from saved claims) + current input percentage
    // This prevents double-counting when the function is called multiple times
    selectedTask[index].percentageComplete = previousPercentageDecimal.plus(exactPercentage).toNumber()

    return { selectedTask, index };
};

/**
 * Handles remaining claim amount checkbox changes.
 * Automatically calculates and sets the remaining percentage and amount when checked.
 * Uses Decimal.js for exact precision calculations.
 * Amount is always the source of truth.
 * 
 * @param {Object} params - Function parameters
 * @param {Event} params.event - Checkbox change event with target.checked
 * @param {string|number} params.taskId - Unique identifier of the task
 * @param {Array} params.selectedTask - Array of task objects to update
 * @returns {Object} Updated selectedTask array and task index, or undefined if task not found
 * 
 * @description
 * When user checks/unchecks the remaining claim checkbox:
 * 1. If checked: Calculates remaining percentage (100% - previous) and corresponding amount
 * 2. Sets amount as source of truth (amount field is primary)
 * 3. Sets percentageComplete to 100%
 * 4. If unchecked: Resets all values to 0
 * 
 * @example
 * const result = updateRemainingClaimAmountChange({
 *   event: { target: { checked: true } },
 *   taskId: 'task-123',
 *   selectedTask: taskArray
 * });
 * // Returns: { selectedTask: updatedArray, index: 0 }
 */
export const updateRemainingClaimAmountChange = ({ event, taskId, selectedTask, progressClaims, progressClaimId, isSectionClaim = false, childTaskMap = {} }) => {
    // Support section-level claims - look up by stageId when isSectionClaim is true
    const index = isSectionClaim
        ? selectedTask.findIndex((t) => t.stageId === taskId || t.id === taskId)
        : selectedTask.findIndex((t) => t.id === taskId);
    if (index === -1) return { selectedTask, index };

    selectedTask[index].claimRemainingAmount = event.target.checked;
    selectedTask[index].isSelected = event.target.checked;

    // For SECTION type, totalBefore is already set from aggregated child data
    const total = selectedTask[index].type === 'SECTION'
        ? (parseFloat(selectedTask[index].totalBefore) || parseFloat(selectedTask[index].sub_total) || 0)
        : (selectedTask[index].totalBefore || 0);

    // Get child task IDs for section claims to aggregate historical claims
    const taskStageId = selectedTask[index].stageId || selectedTask[index].id;
    const childTaskIds = selectedTask[index].type === 'SECTION' ? (childTaskMap[taskStageId] || []) : [];

    // Calculate the actual previous claims amount from progressClaims (fresh calculation)
    // This avoids relying on potentially stale previousPercentageComplete stored value
    const claimAmount = progressClaim({ task: selectedTask[index], progressClaims, progressClaimId, childTaskIds });

    // Calculate previousPercentageComplete fresh from claim history
    const totalDecimal = toDecimal(total);
    const claimAmountDecimal = toDecimal(claimAmount);
    const hundredDecimal = new Decimal(100);

    // Calculate actual previous percentage from financial values
    const calculatedPrevPercentage = totalDecimal.gt(0)
        ? claimAmountDecimal.div(totalDecimal).mul(100)
        : new Decimal(0);

    // Update the stored previousPercentageComplete to keep it in sync
    selectedTask[index].previousPercentageComplete = calculatedPrevPercentage.toNumber();

    if (selectedTask[index].claimRemainingAmount) {
        // Calculate remaining percentage and amount using Decimal
        const remainingPercent = hundredDecimal.minus(calculatedPrevPercentage);
        const remainingAmt = totalDecimal.minus(claimAmountDecimal);

        // Amount is always the source of truth
        selectedTask[index].remainingPercentage = roundValue(remainingPercent.toNumber(), 2);
        selectedTask[index].remainingAmount = roundValue(remainingAmt.toNumber(), 2);
        selectedTask[index].percentageComplete = 100;
    } else {
        selectedTask[index].percentageComplete = calculatedPrevPercentage.toNumber();
        selectedTask[index].remainingPercentage = 0;
        selectedTask[index].remainingAmount = 0;
    }

    return { selectedTask, index };
};